/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// js/helper/currency.js
let currencyFormat = window.themeVariables.settings.currencyCodeEnabled ? window.themeVariables.settings.moneyWithCurrencyFormat : window.themeVariables.settings.moneyFormat;
function formatMoney(cents, format = "") {
  if (typeof cents === "string") {
    cents = cents.replace(".", "");
  }
  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
  function defaultTo(value2, defaultValue) {
    return value2 == null || value2 !== value2 ? defaultValue : value2;
  }
  function formatWithDelimiters(number, precision, thousands, decimal) {
    precision = defaultTo(precision, 2);
    thousands = defaultTo(thousands, ",");
    decimal = defaultTo(decimal, ".");
    if (isNaN(number) || number == null) {
      return 0;
    }
    number = (number / 100).toFixed(precision);
    let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
    return dollarsAmount + centsAmount;
  }
  let value = "";
  switch (formatString.match(placeholderRegex)[1]) {
    case "amount":
      value = formatWithDelimiters(cents, 2);
      break;
    case "amount_no_decimals":
      value = formatWithDelimiters(cents, 0);
      break;
    case "amount_with_space_separator":
      value = formatWithDelimiters(cents, 2, " ", ".");
      break;
    case "amount_with_comma_separator":
      value = formatWithDelimiters(cents, 2, ".", ",");
      break;
    case "amount_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 2, "'", ".");
      break;
    case "amount_no_decimals_with_comma_separator":
      value = formatWithDelimiters(cents, 0, ".", ",");
      break;
    case "amount_no_decimals_with_space_separator":
      value = formatWithDelimiters(cents, 0, " ");
      break;
    case "amount_no_decimals_with_apostrophe_separator":
      value = formatWithDelimiters(cents, 0, "'");
      break;
  }
  if (formatString.indexOf("with_comma_separator") !== -1) {
    return formatString.replace(placeholderRegex, value);
  } else {
    return formatString.replace(placeholderRegex, value);
  }
}

// $('body').on('click','.extra_properties--button:not(.is-selected)',function(){
//   $(this).addClass('is-selected');
//   $('.extra_properties_form').show();
//   $('.select-size_btn,.product-sticky-select-size_btn').hide();
//   $('.product-form__buy-buttons,.product-sticky-form__payment-container').removeClass('hide');
//   $(this).parents('.product-form__option-size').find('.not-show_selected').trigger('click');
// });
// $('body').on('click','.extra_properties--button.is-selected',function(){
//   $(this).removeClass('is-selected');
//   $('.extra_properties_form').hide();
// });

// document.addEventListener('variant:changed', function(event) {
//   let variant = event.detail.variant;
//   let form = event.target;
//   let variant_title = variant.title.toLowerCase();

//   if(variant_title.includes("made to order") || variant.title.includes("MADE TO ORDER")){
//     $('.select-size_btn,.product-sticky-select-size_btn').hide();
//     $('.product-form__buy-buttons,.product-sticky-form__payment-container').removeClass('hide');
//     $(this).parents('.product-form__option-size').find('.not-show_selected').trigger('click');
//   }
// });
$(document).ready(function() {
  $('.featured-product-list_grid.slider').each(function() {
    
    var desktopSlidesToShow = parseInt($(this).attr('desktop_per_row'));
    var mobileSlidesToShow = parseInt($(this).attr('mobile_per_row'));
    
    $(this).slick({
      dots: true,
      slidesToShow: desktopSlidesToShow,
      slidesToScroll: 1,
      infinite: false,
      responsive: [
        {
          breakpoint: 999,
          settings: {
            slidesToShow: mobileSlidesToShow
          }
        }
      ]
    });
  });

  $('.featured-celebrity-closet-grid').slick({
    dots: true,
    slidesToShow: 3,
    slidesToScroll: 1,
    infinite: false
  });
  $('body').on('click','.block-swatch__item.not-show_selected',function(){
   $(this).removeClass('not-show_selected');
   $('.select-size_btn,.product-sticky-select-size_btn').hide();
   $('.product-form__buy-buttons,.product-sticky-form__payment-container').removeClass('hide');
  });
  $('body').on('click','.select-size_btn',function(){
    $('.product-form__option-size').find('.block-swatch-list').addClass('shake');
    setTimeout(function(){ $('.block-swatch-list').removeClass('shake') }, 1000);
  });
  $('body').on('click','.product-sticky-select-size_btn',function(){
    $('html, body').animate({'scrollTop': $('variant-picker').offset().top - 100}, 900);
    $('.product-form__option-size').find('.block-swatch-list').addClass('shake');
    setTimeout(function(){ $('.block-swatch-list').removeClass('shake')}, 1500);
  });
  $('body').on('click','.extra_properties--button:not(.is-selected)',function(){
   $(this).addClass('is-selected');
   $(this).closest('.block-swatch-list').find('.extra_properties_form').addClass('active').show();
  //  $('.extra_properties_form').show();
   $('.select-size_btn,.product-sticky-select-size_btn').hide();
   $('.product-form__buy-buttons,.product-sticky-form__payment-container').removeClass('hide');
   $(this).parents('.product-form__option-size').find('.not-show_selected').trigger('click');
  });
  $('body').on('click','.extra_properties--button.is-selected',function(){
   $(this).removeClass('is-selected');
  //  $('.extra_properties_form').hide();
   $(this).closest('.block-swatch-list').find('.extra_properties_form').removeClass('active').hide();
  });

  function quickviewpriceupdating(_this){
    var selector = _this.closest('.drawer__content');
    var productPrices = selector.querySelector(".price-list");
    
    var variant_price = parseInt(selector.querySelector(".price-list").getAttribute("price"))
    var variant_compare_at_price = parseInt(selector.querySelector(".price-list").getAttribute("compare_at_price"))

    if($(selector).find('[name="made_to_order"]').prop('checked') == true){      
      variant_price += parseInt(selector.querySelector('[name="made_to_order"]').getAttribute('data-price'));
      variant_compare_at_price += parseInt(selector.querySelector('[name="made_to_order"]').getAttribute('data-compprice'));
    }

    if($(selector).find('.prioritize_order input[type="checkbox"]').prop('checked') == true){      
      variant_price += parseInt(selector.querySelector('.prioritize_order input[type="checkbox"]').getAttribute('data-price'));
      variant_compare_at_price += parseInt(selector.querySelector('.prioritize_order input[type="checkbox"]').getAttribute('data-compprice'));
    }

    productPrices.setAttribute('price',parseInt(selector.querySelector(".price-list").getAttribute("price")));

    var prdinnerhtml = '';
    var stickyprdinnerhtml = '';
    if(productPrices.getAttribute('data-pricediscount') != ''){
      prdinnerhtml += `<div class="best-buy_at-price text--xsmall">BEST BUY at</div>`;
    }
    if (variant_compare_at_price > variant_price) {
      productPrices.setAttribute('compare_at_price',parseInt(selector.querySelector(".price-list").getAttribute("compare_at_price")));
      prdinnerhtml += `<span class="price price--highlight"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant_price, currencyFormat)}</span>
      <span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant_compare_at_price, currencyFormat)}</span>`;
    } else {
      prdinnerhtml += `<span class="price"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant_price, currencyFormat)}</span>`;
    }

    productPrices.innerHTML = prdinnerhtml;    
    productPrices.style.display = "";   
  }

  function priceupdating(){
    var productPrices = document.querySelector("[data-product-price-list]");
    
    var variant_price = parseInt(document.querySelector("[data-product-price-list]").getAttribute("price"))
    var variant_compare_at_price = parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price"))

    if($('[name="made_to_order"]').prop('checked') == true){      
      variant_price += parseInt(document.querySelector('[name="made_to_order"]').getAttribute('data-price'));
      variant_compare_at_price += parseInt(document.querySelector('[name="made_to_order"]').getAttribute('data-compprice'));
    }

    if($('.prioritize_order input[type="checkbox"]').prop('checked') == true){      
      variant_price += parseInt(document.querySelector('.prioritize_order input[type="checkbox"]').getAttribute('data-price'));
      variant_compare_at_price += parseInt(document.querySelector('.prioritize_order input[type="checkbox"]').getAttribute('data-compprice'));
    }

    productPrices.setAttribute('price',parseInt(document.querySelector("[data-product-price-list]").getAttribute("price")));

    var prdinnerhtml = '';
    var stickyprdinnerhtml = '';
    if(productPrices.getAttribute('data-pricediscount') != ''){
      prdinnerhtml += `<div class="best-buy_at-price" bis_skin_checked="1">BEST BUY at</div>`;
    }
    if (variant_compare_at_price > variant_price) {
      productPrices.setAttribute('compare_at_price',parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price")));
      prdinnerhtml += `<span class="price price--highlight price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant_price, currencyFormat)}</span>
      <span class="price price--compare"><span class="visually-hidden">${window.themeVariables.strings.productRegularPrice}</span>${formatMoney(variant_compare_at_price, currencyFormat)}</span>`;
      stickyprdinnerhtml = `<span class="product-sticky-form__price" price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("price"))}" compare_at_price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price"))}">${formatMoney(variant_price, currencyFormat)}</span>
                      <span class="product-sticky-form-compare__price" price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("price"))}" compare_at_price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price"))}">${formatMoney(variant_compare_at_price, currencyFormat)}</span>`;
    } else {
      prdinnerhtml += `<span class="price price--large"><span class="visually-hidden">${window.themeVariables.strings.productSalePrice}</span>${formatMoney(variant_price, currencyFormat)}</span>`;
      stickyprdinnerhtml = `<span class="product-sticky-form__price" price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("price"))}" compare_at_price="${parseInt(document.querySelector("[data-product-price-list]").getAttribute("compare_at_price"))}">${formatMoney(variant_price, currencyFormat)}</span>`;
    }
    productPrices.innerHTML = prdinnerhtml;
    document.querySelector(".product-sticky-form__price-list").innerHTML = stickyprdinnerhtml;
    
    productPrices.style.display = "";   
  }

 
  $('body').on('change','[name="made_to_order"]',function(){

    if($(this).closest('.drawer__content').length > 0){
      quickviewpriceupdating(this);
      if($(this).prop('checked') == true){      
        $(this).closest('.product-item').attr('data-madetoorder', 'true');
      }else{
        $(this).closest('.product-item').attr('data-madetoorder', 'false');
      }
    }else{
      priceupdating();
      if($(this).prop('checked') == true){      
        $('body').attr('data-madetoorder', 'true');
      }else{
        $('body').attr('data-madetoorder', 'false');
      }
    }
    
    
  });

  $('body').on('click','.prioritize_order input[type="checkbox"]',function(){
    if($(this).closest('.drawer__content').length > 0){
      quickviewpriceupdating(this);
      if($(this).prop('checked') == true){      
        $(this).closest('.product-item').attr('data-prioritizeorder', 'true');
      }else{
        $(this).closest('.product-item').attr('data-prioritizeorder', 'false');
      }
    }else{
      priceupdating();
    }
  });

  $(".block-swatch-gender__radio").on("change paste keyup", function() {
    $(this).parents('.gender_properties_form').find('.gender-form__option-value').html($(this).val())
  });

    $('.product-offer-header').click(function(){
   $(this).toggleClass('active');
   $(this).next().slideToggle();
  });

  $("body").on("click", ".product-offer-code-block", function () {
    var copy_text = $(this).find('.product-offer-copy-code');
    var text = $(this).find('.product-offer-code').text();
    var $temp = $("<input>");
      
    $("body").append($temp);
    $temp.val(text).select();
    document.execCommand("copy");
    $temp.remove();
    copy_text.html('Copied');
      
    setTimeout(function () {
      copy_text.html('Copy');
    }, 1000);
  });

  const menuItems = document.querySelectorAll('.mega-menu-sec-block');
  const submenuPanels = document.querySelectorAll('.sub-mega-menu-panel');
  const Mainheader = document.querySelectorAll('.header');

  menuItems.forEach(item => {    

    item.addEventListener('mouseenter', () => {
      const index = item.dataset.submenuIndex;
       // Remove active class from all items
      menuItems.forEach(i => i.classList.remove('is-active'));
      // Show matching submenu
      submenuPanels.forEach(panel => {
        panel.hidden = panel.dataset.submenuIndex !== index;
      });

      // Add active class
      item.classList.add('is-active');
    });
  });
  Mainheader.forEach(header => {
  header.addEventListener('mouseleave', () => {
    // Remove 'is-active' from all menu items except the first
    menuItems.forEach((item, index) => {
      if (index !== 0) item.classList.remove('is-active');
    });

    // Hide all submenu panels except the first one
    submenuPanels.forEach((panel, index) => {
      if (index !== 0) panel.hidden = true;
    });
  });
});
  
});

var updated_cart = cart;
document.addEventListener('cart:updated', function(event) {
 var cart = event.detail.cart;
  updated_cart = cart;
  var selected_variant = parseInt($('product-item .disabled_button_div').attr('selected_variant_id'));
  $(event?.detail?.cart.items).each(function( index,item ) {
    if(selected_variant == item.id){
      console.log('selected_variant',selected_variant,item.quantity,item.product_id);
      $('product-item .disabled_button_div').attr("cart_qty",item.quantity);
      $('product-item .quantity-selector__input').attr("max-value",5 - item.quantity);
      if(item.quantity >= 5){
        console.log('if',item.product_id)
        $(`product-item`).addClass('disabled_atc');
        $('product-item .disabled_button_div').addClass('active');
      }else{
        console.log('else',item.product_id)
        $(`product-item[product-id="${item.product_id}"]`).removeClass('disabled_atc');
        $('product-item .disabled_button_div').removeClass('active');
      }
    }
  });
  $(event?.detail?.cart.items).each(function(line_item_index,line_item) {
    $('product-item').each(function( product_item_index,product_item) {
      var product_item_id = parseInt($(this).attr('product-id'));
      if(product_item_id == line_item.product_id){
        if(line_item.quantity >= 5){
          $(this).addClass('disabled_atc');
        }else{
          $(this).removeClass('disabled_atc');
        }
      }
    });
  });
    
  var selected_variant = parseInt($('.product__info .disabled_button_div').attr('selected_variant_id'));
  $('.product__info .product-form__quantity-value').html(1);
  $('.product__info .quantity-selector__input').val(1)
  $('.product__info .quantity-selector__input').attr("max-value",5);
  $('.product__info .disabled_button_div').removeClass('active');
  $('product-sticky-form .disabled_button_div').removeClass('active');
  $(event?.detail?.cart.items).each(function( index,item ) {
    if(selected_variant == item.id){
      console.log('selected_variant',selected_variant,item.quantity,item.product_id);
      $('.product__info .disabled_button_div').attr("cart_qty",item.quantity);
      $('.product__info .quantity-selector__input').attr("max-value",5 - item.quantity);
      if(item.quantity >= 5){
        $('.product__info .disabled_button_div').addClass('active');
        $('product-sticky-form .disabled_button_div').addClass('active');
      }else{
        $('.product__info .disabled_button_div').removeClass('active');
        $('product-sticky-form .disabled_button_div').removeClass('active');
      }
    }else{
      $('.product__info .quantity-selector__input').attr("max-value",5);
      $('.product__info .disabled_button_div').removeClass('active');
      $('product-sticky-form .disabled_button_div').removeClass('active');
    }
  });
});

document.addEventListener('variant:changed', function(event) {
   let variant = event.detail.variant; 
   let form = event.target;
   $('.select-size_btn,.product-sticky-select-size_btn').hide();
   $('.product-form__buy-buttons,.product-sticky-form__payment-container').removeClass('hide');

  $("product-item .disabled_button_div").attr("selected_variant_id",variant.id)
  $(updated_cart.items).each(function( index,item ) {
    if(variant.id == item.id){
      // $('product-item .disabled_button_div').attr("cart_qty",item.quantity);
      // $('product-item .quantity-selector__input').attr("max-value",5 - item.quantity);
      if(item.quantity >= 5){
        setTimeout(function(){ 
          $(`product-item[product-id="${item.product_id}"]`).addClass('disabled_atc');
          $('product-item .disabled_button_div').addClass('active');
        }, 500);
      }else{
        setTimeout(function(){ 
          $(`product-item[product-id="${item.product_id}"]`).removeClass('disabled_atc');
          $('product-item .disabled_button_div').removeClass('active');
        }, 500);
      }
    }
  });

  $('.product__info .product-form__quantity-value').html(1);
  $('.product__info .quantity-selector__input').val(1)
  $(".product__info .disabled_button_div").attr("selected_variant_id",variant.id)
  $(updated_cart.items).each(function( index,item ) {
    if(variant.id == item.id){
      console.log('selected_variant',variant.id,item.quantity,item.product_id);
      $('.product__info .disabled_button_div').attr("cart_qty",item.quantity);
      $('.product__info .quantity-selector__input').attr("max-value",5 - item.quantity);
      if(item.quantity >= 5){
        $('.product__info .disabled_button_div').addClass('active');
        $('product-sticky-form .disabled_button_div').addClass('active');
      }else{
        $('.product__info .disabled_button_div').removeClass('active');
        $('product-sticky-form .disabled_button_div').removeClass('active');
      }
    }else{
      $('.product__info .quantity-selector__input').attr("max-value",5);
      $('.product__info .disabled_button_div').removeClass('active');
      $('product-sticky-form .disabled_button_div').removeClass('active');
    }
  });
});